import React, {ReactNode, useEffect, useMemo} from "react";
import {useQuery} from "@apollo/client";
import gql from "graphql-tag";
import {useHistory} from "react-router";
import ReactDocumentTitle from "../../../components/ReactDocumentTitle";
import AppLayout from "../../../layouts/AppLayout";
import {BrandInvoice, InvoiceOrderByInput, InvoiceState, ReportConfig} from "../../../schema";
import {SortingRule} from "react-table";
import Table, {TableFilter} from "../../../ui/table/Table";
import H1 from "../../../ui/H1";
import {FilterState} from "../../../ui/table/TableFilter";
import {CheckboxFilterBlock} from "../../../ui/table/filter/UICheckboxFilterBlock";
import Modal from "../../../ui/modal/Modal";
import PrimaryButton from "../../../ui/PrimaryButton";
import {ExportInvoiceForm} from "../../../components/brand/ExportInvoiceForm";
import Badge from "../../../ui/Badge";
import InvoicePage from "./InvoicePage";

type TableData = {
  id: string
  date: string
  amount: string
  state: ReactNode
  sentDate?: string
  raw: BrandInvoice
}

export function printInvoiceState(id: string, state: InvoiceState): ReactNode {
  switch (state) {
    case InvoiceState.draft:
      return <Badge type="gray" id={id} caption="draft"/>;
    case InvoiceState.review:
      return <Badge type="yellow" id={id} caption="review"/>;
    case InvoiceState.final:
      return <Badge type="blue" id={id} caption="final"/>;
    case InvoiceState.paid:
      return <Badge type="green" id={id} caption="paid"/>;
    case InvoiceState.cancelled:
      return <Badge type="red" id={id} caption="cancelled"/>;
  }
}

function fromInvoice(invoice: BrandInvoice): TableData {
  const userLocale =
    navigator.languages && navigator.languages.length
      ? navigator.languages[0]
      : navigator.language;
  return {
    id: invoice.id,
    date: invoice.date,
    amount: Intl.NumberFormat(userLocale, {
      currency: invoice.amount.currency, style: 'currency', maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    }).format(invoice.amount.amount),
    state: printInvoiceState(invoice.id, invoice.state),
    sentDate: invoice.sentDate ? invoice.sentDate : undefined,
    raw: invoice,
  }
}

export default function InvoiceListScene() {
  const history = useHistory();

  const skipPageResetRef = React.useRef<boolean>();
  const [invoices, setInvoices] = React.useState<TableData[]>([]);
  const [totalCount, setTotalCount] = React.useState<number>(1);
  const [skip, setSkip] = React.useState<number>(0);
  const [first, setFirst] = React.useState<number>(25);
  const [sortBy, setSortBy] = React.useState<InvoiceOrderByInput>(InvoiceOrderByInput.date_asc);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(undefined);
  const [skipDataLoad, setSkipDataLoad] = React.useState<boolean>(true);
  const [openExport, setOpenExport] = React.useState<boolean>(false);
  const [activeInvoice, setActiveInvoice] = React.useState<BrandInvoice | undefined>(undefined);

  const stateFilters = new CheckboxFilterBlock('state', 'State', [
    {id: InvoiceState.draft, value: InvoiceState.draft, label: 'Draft', checked: false,},
    {id: InvoiceState.review, value: InvoiceState.review, label: 'Review', checked: true,},
    {id: InvoiceState.final, value: InvoiceState.final, label: 'Final', checked: true,},
    {id: InvoiceState.paid, value: InvoiceState.paid, label: 'Paid', checked: true,},
    {id: InvoiceState.cancelled, value: InvoiceState.cancelled, label: 'Cancelled', checked: false,},
  ]);
  const [states, setStates] = React.useState<FilterState[]>([new FilterState(stateFilters.id, false, '')]);
  const stateFilter = states.filter(x => x.id === stateFilters.id && x.active).map(x => x.value).join(',');


  const {data, loading, error} = useQuery<InvoiceQuery>(query, {
    skip: skipDataLoad,
    fetchPolicy: 'cache-and-network',
    variables: {skip, first, sortBy, searchQuery, filterByStates: stateFilter},
  });

  useMemo(() => {
    if (data) {
      setInvoices(data.feed.items.map(x => fromInvoice(x)))
      setTotalCount(data.feed.total);
    }
  }, [data])

  const onFetchData = (pageIndex: number, newPageSize: number, newSortBy: SortingRule<BrandInvoice>[], filter: TableFilter | null) => {
    skipPageResetRef.current = true;
    setSkip(pageIndex * newPageSize);
    setFirst(newPageSize);
    setSearchQuery(filter?.search);
    if (newSortBy.length === 0) {
      setSortBy(InvoiceOrderByInput.date_asc);
    } else {
      const value = newSortBy[0];
      if (value.desc) {
        setSortBy(InvoiceOrderByInput.date_desc);
      } else {
        setSortBy(InvoiceOrderByInput.date_asc)
      }
    }
    setStates(filter?.filters || []);
    setSkipDataLoad(false);
  }

  const onClick = (item: TableData) => {
    setActiveInvoice(item.raw);
  }

  useEffect(() => {
    skipPageResetRef.current = false;
  }, []);


  const columns = React.useMemo(() => [
    {
      Header: 'Invoices',
      columns: [
        {
          Header: 'ID',
          accessor: 'id',
          disableSortBy: true,
        },
        {
          Header: 'Date',
          accessor: 'date',
          defaultSort: 'desc',
          sortType: 'basic',
        },
        {
          Header: 'State',
          accessor: 'state',
          disableSortBy: true,
        },
        {
          Header: 'Amount',
          accessor: 'amount',
          disableSortBy: true,
        },
        {
          Header: 'Invoice sent on',
          accessor: 'sentDate',
          disableSortBy: true,
        },
      ]
    }
  ].flatMap((c: any) => c.columns), []);

  const buttons =
    <PrimaryButton onClick={() => setOpenExport(true)}>Export</PrimaryButton>;

  return (
    <>
      <ReactDocumentTitle title='Invoices'/>
      <AppLayout className="">
        <div className="pt-8 pb-20">
          <H1>Invoices</H1>
          <div className="mt-6 mb-4">
            <Table<TableData, TableFilter> name="invoices-v.03" reset={skipPageResetRef.current || false}
                                           error={error?.message}
                                           loading={loading}
                                           total={totalCount}
                                           onFetchData={onFetchData} columns={columns}
                                           data={invoices}
                                           filters={[stateFilters]}
                                           buttons={buttons}
                                           onClick={onClick}/>
          </div>
          <Modal show={openExport} onClose={() => setOpenExport(false)} title="Export Invoices">
            {data?.reportConfig &&
                <ExportInvoiceForm config={data.reportConfig} onClose={() => setOpenExport(false)}
                                   searchBy={searchQuery} orderBy={sortBy} states={stateFilter}/>
            }
          </Modal>
          {activeInvoice &&
              <Modal show={!!activeInvoice} onClose={() => setActiveInvoice(undefined)}
                     title={`Invoice ${activeInvoice?.id}`}>
                  <InvoicePage invoice={activeInvoice} onClose={() => setActiveInvoice(undefined)}/>
              </Modal>
          }
        </div>
      </AppLayout>
    </>
  )
}

type InvoiceFeed = {
  total: number;
  count: number;
  skip: number;
  items: BrandInvoice[];
}
type InvoiceQuery = {
  feed: InvoiceFeed;
  reportConfig: ReportConfig;
}


const query = gql`
    query InvoiceListQuery($searchQuery: String, $skip: Int!, $first: Int!, $sortBy: InvoiceOrderByInput!, $filterByStates: String) {
        feed: invoices(searchBy: $searchQuery, orderBy: $sortBy, skip: $skip, count: $first, filterByState: $filterByStates) {
            total
            count
            skip

            items {
                id
                eventId
                date
                description
                amount {
                    currency
                    amount
                }
                address {
                    street1
                    street2
                    city
                    province
                    country
                    postcode
                }
                to {
                    name
                    email
                }
                taxId
                organisation
                state
                sentDate
                remoteId
            }
        },
        reportConfig {
            event {
                id
                title
                selected
            },
            invoice {
                id
                title
                selected
            },
        }
    }
`;
